import React, { useState, useEffect } from 'react';

export const ObservationConfigDrawer = ({
  isOpen,
  onClose,
  mapping,
  investigationName,
  onSaveConfig,
}) => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (mapping) {
      setConfig({
        ...mapping,
        prefix: mapping.prefix || '',
        childFlag: !!mapping.childFlag,
        isBold: !!mapping.isBold,
        isUnderLine: !!mapping.isUnderLine,
        sepratePrint: !!mapping.sepratePrint,
        showNabl: mapping.showNabl !== undefined ? !!mapping.showNabl : true,
        isComment: !!mapping.isComment,
        chkHelp: !!mapping.chkHelp,
        microscopy: !!mapping.microscopy,
        isCritical: !!mapping.isCritical,
        isAmr: !!mapping.isAmr,
        isReflex: !!mapping.isReflex,
        isAutoConsume: !!mapping.isAutoConsume,
      });
    }
  }, [mapping]);

  if (!isOpen || !config) return null;

  const handleToggle = (field) => {
    setConfig(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleTextChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSetPrefix = (preset) => {
    setConfig(prev => ({ ...prev, prefix: preset }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSaveConfig(config);
    onClose();
  };

  // Count active automation/special rules for the summary badge
  const activeRulesCount = [
    config.isCritical,
    config.isAmr,
    config.isReflex,
    config.isAutoConsume,
    config.sepratePrint,
    config.isComment,
    config.chkHelp,
    config.microscopy,
  ].filter(Boolean).length;

  return (
    <div className="iom-drawer-overlay" onClick={onClose}>
      <div className="iom-drawer-panel iom-config-dialog" onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="iom-drawer-header">
          <div>
            <div className="iom-drawer-kicker">⚙️ ADVANCED PARAMETER SPECIFICATIONS</div>
            <h3 className="iom-drawer-title">{config.observationName || config.labObservationId}</h3>
            <div className="iom-config-meta-chips">
              <span className="iom-chip-code">ID: {config.labObservationId}</span>
              <span className="iom-chip-format">Format: {config.readingFormat || 'Numeric'}</span>
              {config.unit && <span className="iom-chip-unit">Unit: {config.unit}</span>}
              <span className="iom-chip-parent">Mapped in: {investigationName || 'Current Test'}</span>
            </div>
          </div>
          <button type="button" className="iom-btn-close" onClick={onClose} title="Close configuration">
            ✕
          </button>
        </div>

        {/* Configuration Form Body */}
        <form onSubmit={handleSave} className="iom-config-dialog-body">

          {/* Interactive Live Report Appearance Preview Card */}
          <div className="iom-cfg-preview-card">
            <div className="iom-cfg-preview-header">
              <span className="iom-cfg-preview-badge">👁️ LIVE PATIENT REPORT PREVIEW</span>
              <span className="iom-cfg-preview-sub">Shows how this parameter will render on the final printed report</span>
            </div>
            <div className="iom-cfg-preview-content">
              <div className="iom-cfg-sample-row">
                <div className="iom-cfg-sample-name">
                  <span className="iom-cfg-sample-prefix">{config.prefix}</span>
                  <span
                    style={{
                      fontWeight: config.isBold ? 700 : 500,
                      textDecoration: config.isUnderLine ? 'underline' : 'none',
                    }}
                  >
                    {config.observationName || 'Parameter Name'}
                  </span>
                  {config.childFlag && (
                    <span className="iom-cfg-chip-header">Section Header</span>
                  )}
                </div>

                {!config.childFlag && (
                  <div className="iom-cfg-sample-value">
                    <span className="iom-cfg-sample-num">12.50</span>
                    <span className="iom-cfg-sample-unit">{config.unit || 'mg/dL'}</span>
                    <span className="iom-cfg-sample-ref">[10.0 - 20.0]</span>
                  </div>
                )}

                <div className="iom-cfg-sample-badges">
                  {config.showNabl && (
                    <span className="iom-flag-active iom-flag-nabl" title="ISO 15189 NABL certified">
                      ✓ NABL
                    </span>
                  )}
                  {config.isCritical && (
                    <span className="iom-flag-active iom-flag-critical" title="Panic / Critical value alerting">
                      Critical Alert
                    </span>
                  )}
                  {config.sepratePrint && (
                    <span className="iom-flag-active iom-flag-purple" title="Prints on dedicated section/page">
                      Sep. Page
                    </span>
                  )}
                  {config.isComment && (
                    <span className="iom-flag-active iom-flag-comment" title="Doctor commentary format">
                      Narrative Box
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="iom-cfg-preview-note">
              💡 <em>Note: <strong>Header</strong>, <strong>Bold</strong>, and <strong>Underline</strong> are directly editable via checkboxes on the main mapping table.</em>
            </div>
          </div>

          {/* Group 1: Report Layout & Appearance */}
          <div className="iom-cfg-section-card">
            <div className="iom-cfg-section-title-row">
              <span className="iom-cfg-section-icon">📄</span>
              <div>
                <h4 className="iom-cfg-section-title">1. Report Layout & Styling</h4>
                <p className="iom-cfg-section-desc">Customize indentation, page breaking, and accreditation badges</p>
              </div>
            </div>

            <div className="iom-cfg-fields-grid">
              {/* Visual Prefix */}
              <div className="iom-cfg-box-full">
                <div className="iom-cfg-box-header">
                  <div>
                    <label className="iom-cfg-label">Visual Prefix / Indentation</label>
                    <p className="iom-cfg-hint">Adds visual indentation before the parameter name (useful for sub-parameters like Direct Bilirubin in LFT)</p>
                  </div>
                  <div className="iom-cfg-preset-pills">
                    <button type="button" className="iom-preset-pill" onClick={() => handleSetPrefix('-- ')}>-- </button>
                    <button type="button" className="iom-preset-pill" onClick={() => handleSetPrefix('• ')}>• </button>
                    <button type="button" className="iom-preset-pill" onClick={() => handleSetPrefix('   ')}>Indent (3 spaces)</button>
                    {config.prefix && (
                      <button type="button" className="iom-preset-pill text-danger" onClick={() => handleSetPrefix('')}>Clear</button>
                    )}
                  </div>
                </div>
                <input
                  type="text"
                  className="iom-input iom-prefix-input"
                  placeholder='e.g.  "-- " or "• "'
                  value={config.prefix}
                  onChange={e => handleTextChange('prefix', e.target.value)}
                />
              </div>

              {/* Setting Toggle: Print Separate Page */}
              <div className="iom-cfg-item-card">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Print on Separate Section / Page</div>
                  <div className="iom-cfg-item-desc">
                    Forces this observation and its sub-findings into an isolated report section or dedicated new page.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.sepratePrint}
                    onChange={() => handleToggle('sepratePrint')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Show NABL */}
              <div className="iom-cfg-item-card">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Show NABL Accreditation Mark</div>
                  <div className="iom-cfg-item-desc">
                    Displays the ISO 15189 / NABL certified accreditation mark next to this observation on printed reports.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.showNabl}
                    onChange={() => handleToggle('showNabl')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Comment / Narrative */}
              <div className="iom-cfg-item-card">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Comment / Narrative Parameter</div>
                  <div className="iom-cfg-item-desc">
                    Formats this parameter as an expansive doctor commentary, biopsy finding, or clinical impression box instead of a single number.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isComment}
                    onChange={() => handleToggle('isComment')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>
            </div>
          </div>

          {/* Group 2: Result Entry & Quality Control */}
          <div className="iom-cfg-section-card">
            <div className="iom-cfg-section-title-row">
              <span className="iom-cfg-section-icon">🔒</span>
              <div>
                <h4 className="iom-cfg-section-title">2. Result Entry & Validation Rules</h4>
                <p className="iom-cfg-section-desc">Constrain technician inputs and mandate clinical examination workflows</p>
              </div>
            </div>

            <div className="iom-cfg-fields-grid">
              {/* Setting Toggle: Help Value Only */}
              <div className="iom-cfg-item-card">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Restricted Dropdown Only</div>
                  <div className="iom-cfg-item-desc">
                    Prevents technician from typing arbitrary text. Results must be selected from the configured canned dropdown list (e.g. Positive / Negative / Nil).
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.chkHelp}
                    onChange={() => handleToggle('chkHelp')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Microscopy */}
              <div className="iom-cfg-item-card">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Microscopy Slide Examination</div>
                  <div className="iom-cfg-item-desc">
                    Enables manual morphology slide examination workflow, cell count tracking, and technician slide sign-off.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.microscopy}
                    onChange={() => handleToggle('microscopy')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>
            </div>
          </div>

          {/* Group 3: Automation & Patient Safety */}
          <div className="iom-cfg-section-card">
            <div className="iom-cfg-section-title-row">
              <span className="iom-cfg-section-icon">⚡</span>
              <div>
                <h4 className="iom-cfg-section-title">3. Automation & Clinical Safety Alerts</h4>
                <p className="iom-cfg-section-desc">Automated panic triggers, AMR linearity limits, reflex cascades, and reagent tracking</p>
              </div>
            </div>

            <div className="iom-cfg-fields-grid">
              {/* Setting Toggle: Critical / Panic */}
              <div className="iom-cfg-item-card highlight-critical">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Critical / Panic Value Alerting</div>
                  <div className="iom-cfg-item-desc">
                    Triggers urgent clinical red popups, instant doctor/patient SMS alerts, and audit trail logs whenever dangerous panic limits are breached.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isCritical}
                    onChange={() => handleToggle('isCritical')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: AMR */}
              <div className="iom-cfg-item-card">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Analytical Measurement Range (AMR)</div>
                  <div className="iom-cfg-item-desc">
                    Enforces analyzer linearity limits and alerts technician with sample dilution instructions if result exceeds machine range.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isAmr}
                    onChange={() => handleToggle('isAmr')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Reflex */}
              <div className="iom-cfg-item-card">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Reflex Test Trigger</div>
                  <div className="iom-cfg-item-desc">
                    Enables rule-based cascade testing (e.g. abnormal TSH automatically queues Free T3 & Free T4 reflex testing).
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isReflex}
                    onChange={() => handleToggle('isReflex')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>

              {/* Setting Toggle: Auto Consume */}
              <div className="iom-cfg-item-card">
                <div className="iom-cfg-item-info">
                  <div className="iom-cfg-item-name">Automated Reagent & Inventory Consumption</div>
                  <div className="iom-cfg-item-desc">
                    Automatically deducts linked reagent lot stock from laboratory inventory as soon as results for this observation are verified.
                  </div>
                </div>
                <label className="iom-switch">
                  <input
                    type="checkbox"
                    checked={config.isAutoConsume}
                    onChange={() => handleToggle('isAutoConsume')}
                  />
                  <span className="iom-slider" />
                </label>
              </div>
            </div>
          </div>

          {/* Modal Footer Actions */}
          <div className="iom-drawer-footer iom-config-dialog-footer">
            <div className="iom-footer-summary">
              {activeRulesCount > 0 ? (
                <span className="iom-footer-active-badge">
                  ✓ {activeRulesCount} active rule{activeRulesCount > 1 ? 's' : ''} configured
                </span>
              ) : (
                <span className="text-muted">Standard processing rules active</span>
              )}
            </div>
            <div className="iom-footer-buttons">
              <button
                type="button"
                className="iom-btn iom-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="iom-btn iom-btn-primary"
              >
                Apply Specifications
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ObservationConfigDrawer;
